import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface SelectMenuOption {
  value: string;
  label: ReactNode;
}

export interface SelectMenuAction {
  label: ReactNode;
  /** Phosphor icon class, default "ph-plus". */
  icon?: string;
  onClick: () => void;
}

export interface SelectMenuProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectMenuOption[];
  /** Leading icon in the toggle, e.g. "ph-calendar". */
  icon?: string;
  placeholder?: string;
  /** Optional action item rendered below a divider (e.g. "+ Create schedule"). */
  action?: SelectMenuAction;
  /** Show a search box at the top of the menu that filters options by their (string) label. */
  searchable?: boolean;
  className?: string;
}

/**
 * A styled select built from a button + dropdown menu, with open/close controlled in
 * React (no Bootstrap JS dependency). Supports a leading icon, a highlighted selection,
 * and an optional bottom action item separated by a divider.
 */
export function SelectMenu({
  label,
  value,
  onChange,
  options,
  icon,
  placeholder = 'Select…',
  action,
  searchable = false,
  className = '',
}: SelectMenuProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  const q = query.trim().toLowerCase();
  const visible =
    searchable && q
      ? options.filter((o) => (typeof o.label === 'string' ? o.label.toLowerCase().includes(q) : true))
      : options;

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Default to mb-3 unless the caller supplies their own bottom-margin utility.
  const wrapper = [className.includes('mb-') ? '' : 'mb-3', className].filter(Boolean).join(' ');

  return (
    <div className={wrapper}>
      {label && <label className="form-label">{label}</label>}
      <div className="dropdown" ref={rootRef} style={{ position: 'relative' }}>
        <button
          type="button"
          className="form-control d-flex align-items-center text-start"
          style={{ cursor: 'pointer' }}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {icon && <i className={`${icon} me-2 text-muted`} />}
          <span className="flex-grow-1 text-truncate">
            {selected ? selected.label : <span className="text-muted">{placeholder}</span>}
          </span>
          <i className="ph-caret-down ms-2 text-muted" />
        </button>

        <div
          className={`dropdown-menu ${open ? 'show' : ''}`.trim()}
          style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 320, overflowY: 'auto' }}
        >
          {searchable && (
            <div className="px-2 pb-2">
              <div className="position-relative">
                <i className="ph-magnifying-glass position-absolute text-muted" style={{ left: 10, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  className="form-control form-control-sm ps-4"
                  placeholder="Search"
                  value={query}
                  autoFocus
                  onChange={(e) => setQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}
          {visible.length === 0 && <span className="dropdown-item-text text-muted fs-sm">No matches</span>}
          {visible.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                className={`dropdown-item ${active ? 'bg-primary bg-opacity-10 text-primary fw-semibold' : ''}`.trim()}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </button>
            );
          })}

          {action && (
            <>
              <div className="dropdown-divider" />
              <button
                type="button"
                className="dropdown-item text-primary"
                onClick={() => {
                  action.onClick();
                  setOpen(false);
                }}
              >
                <i className={`${action.icon ?? 'ph-plus'} me-2`} />
                {action.label}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
